/**
 * toc — pure utilities for the Table of Contents / Section Navigator.
 *
 * SRP: data transformation only. No React, no hooks, no I/O.
 * Input is the doc-type skeleton ([sectionNumber, title, wordTarget][]).
 * Output is a typed tree + search utilities consumed by TocPopup.
 */

import { getDepth } from "../lib/sectionHierarchy";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One node in the Table of contents hierarchy. */
export interface TocItem {
	/** Section number string — "1", "5.2", "TITLE". */
	sectionNumber: string;
	/** Human-readable title from the skeleton. */
	title: string;
	/** Word-count target from skeleton tuple[2]. */
	wordTarget: number;
	/** Hierarchy depth: 0 = top-level ("1"), 1 = "1.1", 2 = "1.1.1". */
	depth: number;
	/** Direct children in document order. Empty for leaf sections. */
	children: TocItem[];
}

// ---------------------------------------------------------------------------
// buildTocTree
// ---------------------------------------------------------------------------

/**
 * Convert a flat skeleton array into a hierarchical TocItem tree.
 *
 * Document order is preserved — skeleton order is document order by contract.
 * Orphaned subsections (parent absent in skeleton) become root nodes.
 *
 * @param skeleton - `[sectionNumber, title, wordTarget][]` from DocTypeConfig.
 */
export function buildTocTree(skeleton: [string, string, number][]): TocItem[] {
	const map = new Map<string, TocItem>();
	const roots: TocItem[] = [];

	for (const [sectionNumber, title, wordTarget] of skeleton) {
		const item: TocItem = {
			sectionNumber,
			title,
			wordTarget,
			depth: getDepth(sectionNumber),
			children: [],
		};
		map.set(sectionNumber, item);

		const parentKey = sectionNumber.includes(".")
			? sectionNumber.split(".").slice(0, -1).join(".")
			: null;

		if (parentKey !== null && map.has(parentKey)) {
			// biome-ignore lint/style/noNonNullAssertion: checked by has()
			map.get(parentKey)!.children.push(item);
		} else {
			roots.push(item);
		}
	}

	return roots;
}

// ---------------------------------------------------------------------------
// flattenToc
// ---------------------------------------------------------------------------

/**
 * Depth-first pre-order flattening — parents before children, document order.
 * Used for search mode and keyboard navigation (both need a linear index).
 */
export function flattenToc(tree: TocItem[]): TocItem[] {
	const result: TocItem[] = [];
	function walk(items: TocItem[]) {
		for (const item of items) {
			result.push(item);
			walk(item.children);
		}
	}
	walk(tree);
	return result;
}

// ---------------------------------------------------------------------------
// searchToc
// ---------------------------------------------------------------------------

/**
 * Case-insensitive filter across sectionNumber and title.
 *
 * - "5"      → matches 5, 5.1, 5.2 (number includes)
 * - "ethics" → matches "5 Ethics" (title substring)
 * - "9.4"    → matches 9.4, 9.4.1, 9.4.2 (number prefix)
 * - ""       → returns all items unchanged
 *
 * Returns a flat list in document order; hierarchy is ignored in search results.
 */
export function searchToc(flat: TocItem[], query: string): TocItem[] {
	const q = query.trim().toLowerCase();
	if (!q) return flat;
	return flat.filter(
		(item) =>
			item.sectionNumber.toLowerCase().includes(q) ||
			item.title.toLowerCase().includes(q),
	);
}

// ---------------------------------------------------------------------------
// Collapse initialiser (used by TocPopup)
// ---------------------------------------------------------------------------

/**
 * Build the initial collapsed set for browse mode.
 * Top-level nodes start expanded so all chapters are visible.
 * Depth-1+ nodes that have children start collapsed to keep the list compact.
 */
export function buildInitialCollapsed(tree: TocItem[]): Set<string> {
	const result = new Set<string>();
	for (const root of tree) {
		for (const child of root.children) {
			if (child.children.length > 0) {
				result.add(child.sectionNumber);
			}
		}
	}
	return result;
}

// ---------------------------------------------------------------------------
// Visible-items resolver (used by TocPopup keyboard nav)
// ---------------------------------------------------------------------------

/**
 * Flatten only the visible (non-collapsed) items from the tree.
 * Children of a collapsed node are excluded entirely.
 */
export function visibleTocItems(
	tree: TocItem[],
	collapsed: Set<string>,
): TocItem[] {
	const result: TocItem[] = [];
	function walk(items: TocItem[]) {
		for (const item of items) {
			result.push(item);
			if (!collapsed.has(item.sectionNumber)) {
				walk(item.children);
			}
		}
	}
	walk(tree);
	return result;
}
